/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-25
 */

// Add third-party dependencies.
import { createApi } from '@reduxjs/toolkit/query/react';

// Add local dependencies.
import { baseQueryWithReauth } from '../../api/baseQuery';

// Billing API types
export interface BillingTransaction {
  id: string;
  type: 'payment' | 'refund' | 'chargeback' | 'subscription' | 'invoice';
  status: 'succeeded' | 'pending' | 'failed' | 'canceled';
  amount: number; // Amount in cents
  currency: string;
  description: string;
  payment_method?: {
    type: string;
    card?: {
      brand: string;
      last4: string;
    };
  };
  invoice_id?: string;
  subscription_id?: string;
  created_at: string;
  updated_at: string;
  metadata?: Record<string, string>;
}

export interface BillingInvoice {
  id: string;
  number: string;
  status: 'draft' | 'open' | 'paid' | 'uncollectible' | 'void';
  amount_due: number; // Amount in cents
  amount_paid: number; // Amount in cents
  currency: string;
  period_start: string;
  period_end: string;
  due_date?: string;
  paid_at?: string;
  subscription_id?: string;
  invoice_pdf?: string;
  hosted_invoice_url?: string;
  line_items: {
    id: string;
    description: string;
    amount: number;
    quantity: number;
    plan?: {
      name: string;
      billing_cycle: string;
    };
  }[];
  created_at: string;
  updated_at: string;
}

export interface BillingStatus {
  has_payment_method: boolean;
  default_payment_method?: {
    id: string;
    type: string;
    card?: {
      brand: string;
      last4: string;
    };
  };
  subscription?: {
    id: string;
    status: string;
    current_period_start: string;
    current_period_end: string;
    cancel_at_period_end: boolean;
  };
  upcoming_invoice?: {
    amount_due: number;
    due_date: string;
  };
  past_due_amount: number;
  grace_period_end?: string;
}

export interface SubscriptionTrialStatus {
  has_trial: boolean;
  trial_active: boolean;
  trial_start?: string;
  trial_end?: string;
  trial_days_remaining?: number;
  plan_after_trial?: {
    slug: string;
    name: string;
    price_monthly: number;
  };
}

export interface PaymentFailure {
  id: string;
  type:
    | 'card_declined'
    | 'insufficient_funds'
    | 'expired_card'
    | 'processing_error';
  payment_intent_id: string;
  invoice_id?: string;
  amount: number;
  currency: string;
  failure_reason: string;
  failure_code?: string;
  retry_count: number;
  next_retry_at?: string;
  grace_period_end?: string;
  created_at: string;
}

export interface RetryPaymentRequest {
  payment_intent_id: string;
  payment_method_id?: string;
}

export interface RetryPaymentResponse {
  success: boolean;
  payment_intent_id: string;
  status: string;
  message: string;
}

// Query parameters
export interface TransactionListParams {
  page?: number;
  limit?: number;
  type?: string;
  status?: string;
  start_date?: string;
  end_date?: string;
}

export interface InvoiceListParams {
  page?: number;
  limit?: number;
  status?: string;
  start_date?: string;
  end_date?: string;
}

// API response wrappers
export interface PaginatedResponse<T> {
  count: number;
  next?: string;
  previous?: string;
  results: T[];
}

// Create the billing API slice
export const billingApi = createApi({
  reducerPath: 'billingApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Transaction',
    'Invoice',
    'BillingStatus',
    'TrialStatus',
    'PaymentFailure',
  ],
  endpoints: builder => ({
    // Get transaction history
    getTransactions: builder.query<
      PaginatedResponse<BillingTransaction>,
      TransactionListParams | void
    >({
      query: (params = {}) => ({
        url: 'billing/transactions/',
        method: 'GET',
        params,
      }),
      providesTags: ['Transaction'],
    }),

    // Get single transaction
    getTransaction: builder.query<BillingTransaction, string>({
      query: (transactionId: string) => ({
        url: `billing/transactions/${transactionId}/`,
        method: 'GET',
      }),
      providesTags: ['Transaction'],
    }),

    // Get invoices
    getInvoices: builder.query<
      PaginatedResponse<BillingInvoice>,
      InvoiceListParams | void
    >({
      query: (params = {}) => ({
        url: 'billing/invoices/',
        method: 'GET',
        params,
      }),
      providesTags: ['Invoice'],
    }),

    // Get single invoice
    getInvoice: builder.query<BillingInvoice, string>({
      query: (invoiceId: string) => ({
        url: `billing/invoices/${invoiceId}/`,
        method: 'GET',
      }),
      providesTags: ['Invoice'],
    }),

    // Download invoice PDF
    downloadInvoice: builder.mutation<Blob, string>({
      query: (invoiceId: string) => ({
        url: `billing/invoices/${invoiceId}/download/`,
        method: 'GET',
        responseHandler: 'blob',
      }),
    }),

    // Get billing status
    getBillingStatus: builder.query<BillingStatus, void>({
      query: () => ({
        url: 'billing/status/',
        method: 'GET',
      }),
      providesTags: ['BillingStatus'],
    }),

    // Get trial status
    getTrialStatus: builder.query<SubscriptionTrialStatus, void>({
      query: () => ({
        url: 'billing/trial/status/',
        method: 'GET',
      }),
      providesTags: ['TrialStatus'],
    }),

    // Start trial
    startTrial: builder.mutation<
      SubscriptionTrialStatus,
      { plan_slug: string }
    >({
      query: (data: { plan_slug: string }) => ({
        url: 'billing/trial/start/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['TrialStatus', 'BillingStatus'],
    }),

    // Get payment failures
    getPaymentFailures: builder.query<PaymentFailure[], void>({
      query: () => ({
        url: 'billing/payment-failures/',
        method: 'GET',
      }),
      providesTags: ['PaymentFailure'],
    }),

    // Retry failed payment
    retryPayment: builder.mutation<RetryPaymentResponse, RetryPaymentRequest>({
      query: (data: RetryPaymentRequest) => ({
        url: 'billing/payment/retry/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['PaymentFailure', 'BillingStatus', 'Transaction'],
    }),

    // Update payment method for subscription
    updateSubscriptionPaymentMethod: builder.mutation<
      BillingStatus,
      { payment_method_id: string }
    >({
      query: (data: { payment_method_id: string }) => ({
        url: 'billing/subscription/payment-method/',
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['BillingStatus'],
    }),

    // Cancel subscription
    cancelSubscription: builder.mutation<
      { message: string; cancel_at_period_end: boolean },
      { cancel_at_period_end?: boolean }
    >({
      query: (data = { cancel_at_period_end: true }) => ({
        url: 'billing/subscription/cancel/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['BillingStatus'],
    }),

    // Reactivate subscription
    reactivateSubscription: builder.mutation<BillingStatus, void>({
      query: () => ({
        url: 'billing/subscription/reactivate/',
        method: 'POST',
        body: {},
      }),
      invalidatesTags: ['BillingStatus'],
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  useGetTransactionsQuery,
  useLazyGetTransactionsQuery,
  useGetTransactionQuery,
  useLazyGetTransactionQuery,
  useGetInvoicesQuery,
  useLazyGetInvoicesQuery,
  useGetInvoiceQuery,
  useLazyGetInvoiceQuery,
  useDownloadInvoiceMutation,
  useGetBillingStatusQuery,
  useLazyGetBillingStatusQuery,
  useGetTrialStatusQuery,
  useLazyGetTrialStatusQuery,
  useStartTrialMutation,
  useGetPaymentFailuresQuery,
  useLazyGetPaymentFailuresQuery,
  useRetryPaymentMutation,
  useUpdateSubscriptionPaymentMethodMutation,
  useCancelSubscriptionMutation,
  useReactivateSubscriptionMutation,
} = billingApi;
